import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Stack, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { APP_ICONS } from "../../../assets";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import StatusBlockDialog from "../../../components/Dialogs/StatusBlockDialog";
import DynamicPaginatedTable from "../../../components/Tables/DynamicPaginatedTable";
import TextInput from "../../../components/TextInputs";
import useDebounce from "../../../hooks/useDebounce";
import { setSearchKeys } from "../../../lib/slices/searchKeysSlice";
import { getAllCompanies } from "../../../services/comanyApiFn";
import { updateAllAcountStatus } from "../../../services/commonApiFn";
import { handleOpenSnackBar } from "../../../utils";

const headerData = ["createdAt", "companyDetails", "status"];

const tableRows = ["created_date", "company_details", "status"];

function CompanyLists() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(["client", "common"]);
  const openBlockStatusDialog = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [allCompanies, setAllCompanies] = useState([]);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const debouncedSearchTerm = useDebounce(searchKeys.companyByName, 1000);


  const fetchAllCompanies = async (search) => {
    try {
      setIsLoading(true);
      const params = {
        search: search,
        page: 1,
        limit: 500
      }
      const response = await getAllCompanies(params);
      if (response.data.status === "success") {
        const data = response.data.data.companies;
        setAllCompanies(data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllCompanies(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  const handleOpenConfirmDialog = (e, row) => {
    openBlockStatusDialog.current.openDialog({
      type: "Company",
      status: e ? "Unblock" : "Block",
      data: row,
      event: e,
    });
  };

  const handleUpdateUserStatus = async (params) => {
    try {
      let payload = {
        status: params?.event,
        reason: params.reason,
      };
      setIsLoading(true);
      const response = await updateAllAcountStatus(
        params?.data?._id,
        payload,
        "Company"
      );
      if (response.data.status === "success") {
        fetchAllCompanies(searchKeys.companyByName);
        handleOpenSnackBar("success", response.data.message);
        openBlockStatusDialog.current.closeDialog({});
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigateToDetail = (row, index) => {
    if (!row) return handleOpenSnackBar("error", "No data found");
    navigate(`/company-details/${row._id}`);
  };



  const renderSearchAction = () => {
    return (
      <Stack p={2} direction={{ md: "row" }} bgcolor={"primary.light"}>
        <TextInput
          styleProps={{ width: { xs: "100%", md: "35%", xl: "30%" } }}
          InputStartIcon={<img src={APP_ICONS.searchGreyIcon} width={25} />}
          InputEndIcon={
            searchKeys.companyByName && (
              <CancelOutlinedIcon
                fontSize="small"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  dispatch(
                    setSearchKeys({
                      ...searchKeys,
                      companyByName: "",
                    })
                  );
                }}
              />
            )
          }
          placeholder={t("searchByName", { ns: "common" })}
          value={searchKeys.companyByName}
          onChange={(e) => {
            dispatch(
              setSearchKeys({
                ...searchKeys,
                companyByName: e.target.value,
              })
            );
          }}
        />
      </Stack>
    );
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("companyLists")}
        subtitle={t("manageCompanies")}
        searchActions={renderSearchAction()}
      >
        <DynamicPaginatedTable
          showPagination={true}
          tableHeader={headerData}
          displayRows={tableRows}
          tableData={allCompanies}
          showViewDetail={true}
          handleChangeStatus={handleOpenConfirmDialog}
          handleViewDetail={handleNavigateToDetail}
        />

        {allCompanies?.length === 0 && isLoading === false && (
          <Typography variant="h5" fontWeight={600} textAlign={"center"} my={1}>
            {t("noDataFound", { ns: "common" })}
          </Typography>
        )}
      </PaperContainer>
      <StatusBlockDialog
        ref={openBlockStatusDialog}
        onConfirm={handleUpdateUserStatus}
      />
    </>
  );
}

export default CompanyLists;
